import React, { memo, useRef, useState } from "react";
import { useResizeObserver } from "usehooks-ts";

import { ButtonStandard, Text } from "@performyard/py-components";

import VerticalExpander from "../../../../components/AnimationWrappers/VerticalExpander";

import { config } from "../config";

type ExpandableTextProps = {
	content: string;
	collapseHeight?: number;
	emptyMessage?: string;
	className?: string;
};

/**
 * Expandable text component with smooth animation and Read More/Less button.
 * Used for displaying long text content that can be collapsed.
 * Memoized to prevent unnecessary re-renders.
 */
const ExpandableText = memo(
	({
		content,
		collapseHeight = config.descriptionCollapseHeight,
		emptyMessage = "No content",
		className,
	}: ExpandableTextProps): React.ReactElement => {
		const [isExpanded, setIsExpanded] = useState(false);
		const contentRef = useRef<HTMLDivElement | null>(null);
		const { height: contentHeight = 0 } = useResizeObserver({
			ref: contentRef as React.RefObject<HTMLDivElement>,
		});

		const hasContent = Boolean(content?.trim());
		const showReadMore = contentHeight > collapseHeight;

		if (!hasContent) {
			return (
				<Text size="paragraph-3" weight="regular" color="neutral-5">
					{emptyMessage}
				</Text>
			);
		}

		return (
			<div className={className}>
				<VerticalExpander isExpanded={isExpanded} closedHeight={collapseHeight}>
					<div ref={contentRef}>
						<Text size="paragraph-3" weight="regular" dangerouslySetClassName="whitespace-pre-wrap">
							{content}
						</Text>
					</div>
				</VerticalExpander>
				{showReadMore && (
					<div className="w-fit pt-2">
						<ButtonStandard
							variant="micro"
							color="neutral"
							onClick={() => setIsExpanded(!isExpanded)}
							dangerouslySetClassName="bg-neutral-2 hover:bg-neutral-5 active:bg-neutral-8 active:text-neutral-0 text-neutral-9"
						>
							{isExpanded ? "Read Less" : "Read More"}
						</ButtonStandard>
					</div>
				)}
			</div>
		);
	}
);

ExpandableText.displayName = "ExpandableText";

export default ExpandableText;
